import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { OsintSearchService } from '../../../services/osint-search/osint-search.service';
import { GstSearchService } from '../../../services/gst/gst.service';
import { DinSearchService } from '../../../services/din/din.service';
import {
  createPlatformIconDataUrl,
  resolvePlatformIconVisual,
} from '../../../services/link-analysis/platform-icon.util';
import {
  SearchResultProcessorService,
  SearchImage,
} from '../../../services/search-result-processor/search-result-processor.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { AbacusStructuredValueComponent } from '../shared/abacus-structured-value/abacus-structured-value.component';

const PRINTABLE_STATE_PREFIX = 'osint-search-printable-';

/** A4 at 150 DPI: 210mm × 297mm → 1240 × 1752 px */
const A4_DPI = 150;
const A4_WIDTH_PX = Math.round((210 / 25.4) * A4_DPI);
const A4_HEIGHT_PX = Math.round((297 / 25.4) * A4_DPI);
const PAGE_PADDING_PX = Math.round((12 / 25.4) * A4_DPI);
const PAGE_FOOTER_PX = 48;
const CONTENT_MAX_HEIGHT_PX =
  A4_HEIGHT_PX - PAGE_PADDING_PX * 2 - PAGE_FOOTER_PX;
const PHOTOS_PER_ROW = 4;
/** Must be a multiple of the 3-column names grid so each box fills complete rows. */
const NAMES_PER_CHUNK = 12;
/** Must be a multiple of the 3-column emails grid so each box fills complete rows. */
const EMAILS_PER_CHUNK = 12;
const PLATFORM_COLUMN_COUNT = 3;
/** Extra room so estimated layouts never paint past the A4 content box. */
const PAGE_PACK_SAFETY_PX = 48;

export type OsintPrintableSearchType =
  | 'username'
  | 'phone'
  | 'name'
  | 'email'
  | 'gst'
  | 'din';

export interface OsintPrintableTypeConfig {
  apiType: OsintPrintableSearchType;
  /** Primary field on the search result object. */
  queryField: string;
  /** Extra fields checked when resolving the displayed query. */
  queryFallbacks: string[];
  reportTitle: string;
  reportSubtitle: string;
  closeRoute: string;
  queryParamKey: string;
}

export const OSINT_PRINTABLE_TYPE_CONFIG: Record<
  OsintPrintableSearchType,
  OsintPrintableTypeConfig
> = {
  username: {
    apiType: 'username',
    queryField: 'searched_username',
    queryFallbacks: ['username'],
    reportTitle: 'OSINT Username Report',
    reportSubtitle: 'Username intelligence report',
    closeRoute: '/osint-username-search',
    queryParamKey: 'username',
  },
  phone: {
    apiType: 'phone',
    queryField: 'searched_phone',
    queryFallbacks: ['number', 'phone'],
    reportTitle: 'OSINT Phone Report',
    reportSubtitle: 'Phone intelligence report',
    closeRoute: '/osint-number-search',
    queryParamKey: 'phone',
  },
  name: {
    apiType: 'name',
    queryField: 'searched_name',
    // Do not fall back to `name` — that field is the aggregated matches array.
    queryFallbacks: [],
    reportTitle: 'OSINT Name Report',
    reportSubtitle: 'Name intelligence report',
    closeRoute: '/osint-name-search',
    queryParamKey: 'name',
  },
  email: {
    apiType: 'email',
    queryField: 'searched_email',
    queryFallbacks: ['email'],
    reportTitle: 'OSINT Email Report',
    reportSubtitle: 'Email intelligence report',
    closeRoute: '/osint-email-search',
    queryParamKey: 'email',
  },
  gst: {
    apiType: 'gst',
    queryField: 'searched_gst',
    queryFallbacks: ['gst', 'gstin'],
    reportTitle: 'GST Report',
    reportSubtitle: 'Company registry & contacts',
    closeRoute: '/gst-search',
    queryParamKey: 'gst',
  },
  din: {
    apiType: 'din',
    queryField: 'searched_din',
    queryFallbacks: ['din'],
    reportTitle: 'DIN Report',
    reportSubtitle: 'Director registry & contacts',
    closeRoute: '/din-search',
    queryParamKey: 'din',
  },
};

/** Query fields must be scalars. Arrays (e.g. aggregated `name` matches) must not become the title. */
function readScalarQueryValue(source: any, fields: string[]): string {
  if (!source) {
    return '';
  }
  for (const field of fields) {
    const value = source?.[field];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
}

export interface OsintPrintableDialogData {
  searchResult: any;
  searchId?: string;
  routeId?: string;
  searchType: OsintPrintableSearchType;
}

/** @deprecated Use OsintPrintableDialogData */
export type OsintUsernamePrintableDialogData = OsintPrintableDialogData;

export type PrintBlockType =
  | 'header'
  | 'status'
  | 'photos'
  | 'originalPhoto'
  | 'names'
  | 'emails'
  | 'emailHints'
  | 'platform'
  | 'platforms';

export interface PrintBlock {
  key: string;
  type: PrintBlockType;
  images?: SearchImage[];
  /** Single image for original-size appendix blocks. */
  image?: SearchImage;
  names?: string[];
  /** Names packed beside an incomplete photo row (1–3 photos). */
  fillNames?: string[];
  emails?: string[];
  platform?: any;
  platforms?: any[];
  /** Explicit column buckets so render height matches the packer exactly. */
  platformColumns?: any[][];
  showPhotosHeading?: boolean;
  showOriginalPhotosHeading?: boolean;
  showNamesHeading?: boolean;
  showEmailsHeading?: boolean;
}

export interface PrintPage {
  pageNumber: number;
  blocks: PrintBlock[];
  /** When a single block exceeds A4 content height, grow the sheet instead of clipping. */
  allowGrow: boolean;
}

@Component({
  selector: 'app-osint-search-printable',
  standalone: true,
  imports: [CommonModule, RouterModule, AbacusStructuredValueComponent],
  templateUrl: './osint-search-printable.component.html',
  styleUrls: [
    '../osint-username-search-result/osint-username-search-result.component.css',
    './osint-search-printable.component.css',
  ],
})
export class OsintSearchPrintableComponent
  implements OnInit, OnDestroy, AfterViewInit {
  readonly a4WidthPx = A4_WIDTH_PX;
  readonly a4HeightPx = A4_HEIGHT_PX;
  readonly a4Dpi = A4_DPI;
  readonly pagePaddingPx = PAGE_PADDING_PX;

  selectedSearchResult: any;
  searchId: string = '';
  searchType: OsintPrintableSearchType = 'username';
  noRecords = false;
  loadingCompletedSearchResult = false;
  loadingImages = false;
  isPaginating = false;
  pagesReady = false;

  searchResultImages: SearchImage[] = [];
  searchResultDetails: any[] = [];
  aggregatedNames: string[] = [];
  aggregatedEmails: string[] = [];
  aggregatedEmailHints: string[] = [];

  measureBlocks: PrintBlock[] = [];
  pages: PrintPage[] = [];
  /** True when opened from MatDialog instead of a routed page. */
  isDialogMode = false;
  /** Logged-in user who generated this PDF preview. */
  generatedBy = '';
  /** Local timestamp when the printable preview was opened. */
  generatedAt = '';

  @ViewChildren('measureBlock')
  private measureBlockEls?: QueryList<ElementRef<HTMLElement>>;

  private processedIcons: WeakSet<HTMLImageElement> = new WeakSet();
  private loadSubscription?: Subscription;
  private imagesSubscription?: Subscription;
  private paginationTimer?: ReturnType<typeof setTimeout>;
  private viewReady = false;
  private pendingPagination = false;

  get typeConfig(): OsintPrintableTypeConfig {
    return OSINT_PRINTABLE_TYPE_CONFIG[this.searchType];
  }

  get queryValue(): string {
    return this.readQueryValue(this.selectedSearchResult);
  }

  get reportTitle(): string {
    return this.typeConfig.reportTitle;
  }

  get reportSubtitle(): string {
    return this.typeConfig.reportSubtitle;
  }

  get isLoadingResults(): boolean {
    const hasResults =
      (this.searchResultDetails?.length > 0 ||
        this.searchResultImages?.length > 0) &&
      !this.loadingImages;
    return this.loadingCompletedSearchResult && !hasResults;
  }

  get namesCount(): number {
    return this.aggregatedNames?.length ?? 0;
  }

  get photosCount(): number {
    return this.searchResultImages?.length ?? 0;
  }

  get emailsCount(): number {
    return this.aggregatedEmails?.length ?? 0;
  }

  get emailHintsCount(): number {
    return this.aggregatedEmailHints?.length ?? 0;
  }

  get pageSheetStyle(): Record<string, string> {
    return {
      width: `${this.a4WidthPx}px`,
      height: `${this.a4HeightPx}px`,
      padding: `${this.pagePaddingPx}px`,
    };
  }

  get measureSheetStyle(): Record<string, string> {
    return {
      width: `${this.a4WidthPx}px`,
      padding: `${this.pagePaddingPx}px`,
    };
  }

  getPageSheetStyle(page: PrintPage): Record<string, string> {
    if (page.allowGrow) {
      return {
        width: `${this.a4WidthPx}px`,
        minHeight: `${this.a4HeightPx}px`,
        height: 'auto',
        padding: `${this.pagePaddingPx}px`,
      };
    }
    return this.pageSheetStyle;
  }

  /** Match multi-column card width so measured height matches page layout. */
  get platformColumnWidthPx(): number {
    const contentWidth = this.a4WidthPx - this.pagePaddingPx * 2;
    // GST/DIN registry cards are full-width on web and in PDF.
    if (this.isRegistryPrintable) {
      return Math.max(200, contentWidth);
    }
    const gapPx = Math.round(0.75 * 16);
    return Math.max(200, Math.floor((contentWidth - 2 * gapPx) / 3));
  }

  get isGstPrintable(): boolean {
    return this.searchType === 'gst';
  }

  get isDinPrintable(): boolean {
    return this.searchType === 'din';
  }

  get isRegistryPrintable(): boolean {
    return this.isGstPrintable || this.isDinPrintable;
  }

  usesTwoColumnPlatformCards(platforms: any[] | undefined): boolean {
    return (platforms?.length ?? 0) === 2;
  }

  /**
   * Fastest exact-look export: native browser Print → Save as PDF.
   * Sets document.title so the Save dialog suggests a useful PDF filename.
   */
  downloadPdf(): void {
    if (!this.pagesReady || !this.pages.length) {
      return;
    }

    // Drop focus so contenteditable carets/outlines don't appear in the PDF.
    const active = document.activeElement as HTMLElement | null;
    active?.blur?.();

    const previousTitle = document.title;
    document.title = this.buildPdfFileName();

    const cleanup = () => {
      document.title = previousTitle;
      document.body.classList.remove(
        'osint-printable-printing',
        'osint-printable-dialog-open',
      );
      window.removeEventListener('afterprint', cleanup);
    };

    document.body.classList.add('osint-printable-printing');
    if (this.isDialogMode) {
      // Dialog content lives in .cdk-overlay-container, not app-root.
      document.body.classList.add('osint-printable-dialog-open');
    }
    window.addEventListener('afterprint', cleanup);

    // Let layout apply print classes before the browser captures pages.
    requestAnimationFrame(() => {
      try {
        window.print();
      } catch {
        cleanup();
      }
    });
  }

  /** e.g. osint-username-narrr-report */
  private buildPdfFileName(): string {
    const typeSlug =
      this.searchType === 'phone' ? 'number' : this.searchType || 'search';
    const querySlug = this.slugifyForFileName(this.queryValue) || 'report';
    return `osint-${typeSlug}-${querySlug}-report`;
  }

  private slugifyForFileName(value: string): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }

  /** Keep links from navigating while editing the printable preview. */
  onEditableClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a')) {
      event.preventDefault();
    }
  }

  closePreview(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    void this.router.navigate([this.typeConfig.closeRoute]);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private osintSearchService: OsintSearchService,
    private gstSearchService: GstSearchService,
    private dinSearchService: DinSearchService,
    private searchResultProcessorService: SearchResultProcessorService,
    public utilsService: UtilsService,
    private cdr: ChangeDetectorRef,
    @Optional()
    private dialogRef: MatDialogRef<OsintSearchPrintableComponent> | null,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    private dialogData: OsintPrintableDialogData | null,
  ) { }

  ngOnInit(): void {
    this.generatedBy = this.resolveGeneratedBy();
    this.generatedAt = this.formatGeneratedAt(new Date());

    const routeType = this.route.snapshot.data?.['searchType'] as
      | OsintPrintableSearchType
      | undefined;
    const queryType = this.route.snapshot.queryParamMap.get('type') as
      | OsintPrintableSearchType
      | null;

    if (this.dialogData?.searchResult) {
      this.isDialogMode = true;
      this.searchType = this.dialogData.searchType || routeType || 'username';
      this.selectedSearchResult = {
        ...this.dialogData.searchResult,
        done: true,
      };
      this.searchId =
        this.dialogData.searchId ||
        this.selectedSearchResult.din_search_id ||
        this.selectedSearchResult.gst_search_id ||
        this.selectedSearchResult.osint_search_id ||
        this.selectedSearchResult.search_id ||
        '';
      this.getCompletedSearchResult();
      return;
    }

    this.searchType = routeType || queryType || 'username';
    const routeId = this.route.snapshot.paramMap.get('id') || '';
    const querySearchId =
      this.route.snapshot.queryParamMap.get('searchId') || '';
    const queryValue =
      this.route.snapshot.queryParamMap.get(this.typeConfig.queryParamKey) ||
      '';
    this.selectedSearchResult = this.resolveSearchResult(
      routeId,
      querySearchId,
      queryValue,
    );

    if (!this.selectedSearchResult) {
      this.router.navigate([this.typeConfig.closeRoute]);
      return;
    }

    if (queryValue && !this.readQueryValue(this.selectedSearchResult)) {
      this.selectedSearchResult = {
        ...this.selectedSearchResult,
        [this.typeConfig.queryField]: queryValue,
      };
    }

    this.searchId =
      this.selectedSearchResult.din_search_id ||
      this.selectedSearchResult.gst_search_id ||
      this.selectedSearchResult.osint_search_id ||
      this.selectedSearchResult.search_id ||
      querySearchId ||
      routeId;

    this.getCompletedSearchResult();
  }

  private resolveGeneratedBy(): string {
    try {
      const raw = localStorage.getItem('USER_PORTAL');
      const userPortal = raw ? JSON.parse(raw) : null;
      const details = userPortal?.loginDetails || {};
      return (
        String(details.user_name || details.user_email || details.email || '')
          .trim() || 'Unknown user'
      );
    } catch {
      return 'Unknown user';
    }
  }

  private formatGeneratedAt(date: Date): string {
    try {
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return date.toISOString();
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.pendingPagination) {
      this.schedulePagination();
    }
  }

  ngOnDestroy(): void {
    if (this.paginationTimer) {
      clearTimeout(this.paginationTimer);
    }
    this.loadSubscription?.unsubscribe();
    this.imagesSubscription?.unsubscribe();
    this.searchResultProcessorService.cleanupBlobUrls();
  }

  /** Columns available for names in the leftover photo-row space. */
  private photoFillNameColumns(photoCountInRow: number): number {
    // Only fill beside 1–2 photos. With 3 photos the leftover column is too
    // narrow and name text truncates.
    // Wider name columns so text stays readable:
    // 1 photo → 2 name cols, 2 photos → 1 name col.
    if (photoCountInRow === 1) {
      return 2;
    }
    if (photoCountInRow === 2) {
      return 1;
    }
    return 0;
  }

  /** Pack the first Names box full before overflowing to the next section. */
  private namesToFillBesidePhotos(photoCountInRow: number): number {
    const columns = this.photoFillNameColumns(photoCountInRow);
    if (!columns) {
      return 0;
    }
    // ~4 rows fit the square photo + caption height with compact name cards.
    const rows = 4;
    return columns * rows; // 8 / 4
  }

  hasPhotoNameFill(block: PrintBlock): boolean {
    return (block.fillNames?.length ?? 0) > 0;
  }

  getPhotosGridStyle(block: PrintBlock): Record<string, string> {
    const count = Math.max(1, block.images?.length ?? 1);
    if (!this.hasPhotoNameFill(block)) {
      return {
        gridTemplateColumns: `repeat(${PHOTOS_PER_ROW}, minmax(0, 1fr))`,
      };
    }
    return {
      gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
      flex: `0 0 calc(${(count / PHOTOS_PER_ROW) * 100}% - 0.35rem)`,
      maxWidth: `calc(${(count / PHOTOS_PER_ROW) * 100}% - 0.35rem)`,
    };
  }

  getPhotoFillNamesGridStyle(block: PrintBlock): Record<string, string> {
    const columns = Math.max(
      1,
      this.photoFillNameColumns(block.images?.length ?? 0),
    );
    return {
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    };
  }

  trackByBlockKey(_: number, block: PrintBlock): string {
    return block.key;
  }

  trackByPageNumber(_: number, page: PrintPage): number {
    return page.pageNumber;
  }

  onIconError(event: Event): void {
    const imgElement = event.target as HTMLImageElement | null;
    if (!imgElement) {
      return;
    }

    if (this.processedIcons.has(imgElement)) {
      imgElement.style.display = 'none';
      return;
    }

    const defaultIcon = 'assets/icons/default.png';
    const currentSrc = imgElement.src || '';

    if (currentSrc.includes('default.png')) {
      this.processedIcons.add(imgElement);
      imgElement.style.display = 'none';
      return;
    }

    this.processedIcons.add(imgElement);
    imgElement.src = defaultIcon;
  }

  getPlatformImage(platform: string): SearchImage | undefined {
    const normalizedPlatform = this.normalizePlatformNameForLookup(platform);
    return this.searchResultImages.find(
      (image) =>
        this.normalizePlatformNameForLookup(image.platform) ===
        normalizedPlatform,
    );
  }

  getPlatformImageForCard(platformResult: any): SearchImage | undefined {
    const src = String(platformResult?.imageUrl || '').trim();
    if (src) {
      return {
        platform: platformResult?.platform || 'Platform',
        src,
        name: platformResult?.platform || 'Platform',
        height: 150,
      };
    }
    return (
      this.getPlatformImage(platformResult?.platformKey) ||
      this.getPlatformImage(platformResult?.platform)
    );
  }

  getPlatformThemeStyle(platform: string): Record<string, string> {
    const accent = resolvePlatformIconVisual(platform).iconBg || '#38bdf8';
    const hex = accent.replace('#', '');
    const normalizedHex =
      hex.length === 3
        ? hex
          .split('')
          .map((character) => character + character)
          .join('')
        : hex;
    const colorValue = Number.parseInt(normalizedHex, 16);
    const isValidHex = normalizedHex.length === 6 && !Number.isNaN(colorValue);
    const red = isValidHex ? (colorValue >> 16) & 255 : 56;
    const green = isValidHex ? (colorValue >> 8) & 255 : 189;
    const blue = isValidHex ? colorValue & 255 : 248;

    return {
      '--source-accent': isValidHex ? accent : '#38bdf8',
      '--source-accent-rgb': `${red} ${green} ${blue}`,
    };
  }

  isDataDenseSourceCard(platformResult: any): boolean {
    const detailCount = (platformResult?.entries ?? []).reduce(
      (count: number, entry: any) =>
        count +
        (entry?.details ?? []).filter(
          (detail: any) => detail?.type !== 'base64String',
        ).length,
      0,
    );

    return detailCount > 6;
  }

  getPlatformCategory(platform: string): string {
    const key = String(platform || '')
      .toLowerCase()
      .trim();

    if (
      key.includes('youtube') ||
      key.includes('twitch') ||
      key.includes('vimeo') ||
      key.includes('bitchute') ||
      key.includes('trovo') ||
      key.includes('periscope') ||
      key.includes('jeuxvideo') ||
      key.includes('dailymotion')
    ) {
      return 'Video & Streaming';
    }
    if (
      key.includes('telegram') ||
      key.includes('whatsapp') ||
      key.includes('signal') ||
      key.includes('skype') ||
      key.includes('viber') ||
      key.includes('discord')
    ) {
      return 'Messaging';
    }
    if (
      key.includes('spotify') ||
      key.includes('soundcloud') ||
      key.includes('deezer') ||
      key.includes('last.fm') ||
      key.includes('lastfm') ||
      key.includes('apple music') ||
      key.includes('pandora')
    ) {
      return 'Music';
    }
    if (
      key.includes('reddit') ||
      key.includes('quora') ||
      key.includes('stackoverflow') ||
      key.includes('stack overflow') ||
      key.includes('forum') ||
      key.includes('discourse')
    ) {
      return 'Forums & Community';
    }
    if (
      key.includes('linkedin') ||
      key.includes('github') ||
      key.includes('gitlab') ||
      key.includes('bitbucket') ||
      key.includes('indeed') ||
      key.includes('glassdoor') ||
      key.includes('behance') ||
      key.includes('dribbble')
    ) {
      return 'Professional';
    }
    if (
      key.includes('facebook') ||
      key.includes('instagram') ||
      key.includes('twitter') ||
      key === 'x' ||
      key.includes('snapchat') ||
      key.includes('tiktok') ||
      key.includes('pinterest') ||
      key.includes('tumblr')
    ) {
      return 'Social Media';
    }

    return 'Other';
  }

  getFlattenedDetails(platformResult: any): any[] {
    const details: any[] = [];
    for (const entry of platformResult?.entries ?? []) {
      for (const detail of entry?.details ?? []) {
        if (
          detail?.type === 'base64String' ||
          this.searchResultProcessorService.isHiddenDisplayField(detail?.label) ||
          this.utilsService.isNonRenderableMetadataValue(detail?.value)
        ) {
          continue;
        }
        details.push(detail);
      }
    }
    return details;
  }

  getProfileLinkDetail(platformResult: any): any | null {
    const details = this.getFlattenedDetails(platformResult);
    const preferred = details.find((detail) => {
      const label = String(detail?.label || '')
        .toLowerCase()
        .trim();
      return (
        detail?.type === 'link' &&
        (label.includes('profile') || label.includes('url') || label === 'link')
      );
    });
    if (preferred) {
      return preferred;
    }
    const anyLink = details.find((detail) => detail?.type === 'link');
    if (anyLink) {
      return anyLink;
    }
    const profileUrl = String(platformResult?.profile_url || '').trim();
    if (profileUrl) {
      return {
        label: 'Profile Url',
        value: profileUrl,
        type: 'link',
      };
    }
    return null;
  }

  getCardDescription(platformResult: any): string | null {
    const details = this.getFlattenedDetails(platformResult);
    const descriptionDetail = details.find((detail) => {
      const label = String(detail?.label || '')
        .toLowerCase()
        .trim();
      return label === 'description' || label === 'bio' || label === 'about';
    });
    if (!descriptionDetail || this.isEmptyValue(descriptionDetail.value)) {
      return null;
    }
    const text = this.utilsService.formatDisplayValue(descriptionDetail.value);
    return text && text !== 'N/A' ? text : null;
  }

  getCardBodyDetails(platformResult: any): any[] {
    const profileLink = this.getProfileLinkDetail(platformResult);
    const description = this.getCardDescription(platformResult);

    return this.getFlattenedDetails(platformResult).filter((detail) => {
      if (profileLink && detail === profileLink) {
        return false;
      }
      const label = String(detail?.label || '')
        .toLowerCase()
        .trim();
      if (
        label === 'description' ||
        label === 'bio' ||
        label === 'about' ||
        label === 'category' ||
        label === 'category name' ||
        label === 'category type' ||
        label.includes('picture') ||
        label.includes('photo') ||
        label.includes('avatar') ||
        label.includes('image')
      ) {
        return false;
      }
      if (description && detail?.type !== 'link') {
        const formatted = this.utilsService.formatDisplayValue(detail?.value);
        if (formatted === description) {
          return false;
        }
      }
      if (this.isBlankPlaceholder(detail?.value)) {
        return false;
      }
      return true;
    });
  }

  /** Resolve a GST field detail by label/key (mirrors web GST result page). */
  getGstDetail(platformResult: any, ...keys: string[]): any | null {
    const wanted = keys.map((k) =>
      String(k || '')
        .toLowerCase()
        .replace(/[\s-]+/g, '_'),
    );
    const details = this.getFlattenedDetails(platformResult);
    return (
      details.find((detail) => {
        const label = String(detail?.label || detail?.key || '')
          .toLowerCase()
          .replace(/[\s-]+/g, '_');
        return wanted.some((k) => label === k || label.includes(k));
      }) ?? null
    );
  }

  getGstValue(platformResult: any, ...keys: string[]): string {
    const detail = this.getGstDetail(platformResult, ...keys);
    if (!detail || this.isEmptyValue(detail.value)) {
      return '';
    }
    if (Array.isArray(detail.value)) {
      return detail.value
        .map((v: any) => String(v ?? '').trim())
        .filter(Boolean)
        .join(', ');
    }
    return this.utilsService.formatDisplayValue(detail.value);
  }

  getGstNatureTags(platformResult: any): string[] {
    const detail = this.getGstDetail(
      platformResult,
      'nature_of_business',
      'nature_of_business_activities',
    );
    if (!detail || this.isEmptyValue(detail.value)) {
      return [];
    }
    if (Array.isArray(detail.value)) {
      return detail.value
        .map((v: any) => String(v ?? '').trim())
        .filter(Boolean);
    }
    const raw = String(detail.value ?? '').trim();
    if (!raw) return [];
    return raw
      .split(/[,;|]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  getGstLegalName(platformResult: any): string {
    const raw =
      this.getGstValue(platformResult, 'legal_name', 'company_name') ||
      String(this.aggregatedNames?.[0] || '').trim() ||
      '—';
    return this.formatGstDisplayName(raw);
  }

  private formatGstDisplayName(value: string): string {
    const s = String(value || '').trim();
    if (!s || s === '—') return s;
    const letters = s.replace(/[^A-Za-z]/g, '');
    if (letters && letters === letters.toUpperCase()) {
      return s
        .toLowerCase()
        .replace(/\b([a-z])/g, (m) => m.toUpperCase());
    }
    return s;
  }

  getGstEntityBadge(platformResult: any): string {
    const entity = this.getGstValue(platformResult, 'entity_type');
    const taxpayer = this.getGstValue(platformResult, 'taxpayer_type');
    if (entity && taxpayer) {
      return `${entity} · ${taxpayer} taxpayer`;
    }
    if (entity) return entity;
    if (taxpayer) return `${taxpayer} taxpayer`;
    return '';
  }

  getGstStatusLabel(platformResult: any): string {
    return this.getGstValue(platformResult, 'status') || 'Unknown';
  }

  isGstStatusActive(platformResult: any): boolean {
    return /active/i.test(this.getGstStatusLabel(platformResult));
  }

  /** Prefer raw persisted sources.din — processor flattens object-lists to strings. */
  private getRawDinSource(): any {
    const sources = this.selectedSearchResult?.sources;
    if (sources?.din && typeof sources.din === 'object') {
      return sources.din;
    }
    return null;
  }

  private getRawDinFieldValue(...keys: string[]): any {
    const fields = this.getRawDinSource()?.fields;
    if (!fields || typeof fields !== 'object') return null;
    for (const key of keys) {
      const field = fields[key];
      if (!field) continue;
      const value = field?.value !== undefined ? field.value : field;
      if (value === null || value === undefined) continue;
      if (typeof value === 'string' && (!value.trim() || value.trim() === 'N/A')) {
        continue;
      }
      return value;
    }
    return null;
  }

  private unwrapDinFieldValue(value: any): any {
    if (value && typeof value === 'object' && 'value' in value && !Array.isArray(value)) {
      return value.value;
    }
    return value;
  }

  /** Resolve a DIN field detail by label/key (mirrors web DIN result page). */
  getDinDetail(platformResult: any, ...keys: string[]): any | null {
    const wanted = keys.map((k) =>
      String(k || '')
        .toLowerCase()
        .replace(/[\s-]+/g, '_'),
    );
    const details = this.getFlattenedDetails(platformResult);
    const matches = details.filter((detail) => {
      const label = String(detail?.label || detail?.key || '')
        .toLowerCase()
        .replace(/[\s-]+/g, '_');
      return wanted.some((k) => label === k || label.includes(k));
    });
    const meaningful = matches.find((detail) => {
      const v = detail?.value;
      if (v === null || v === undefined) return false;
      if (typeof v === 'string') {
        const t = v.trim();
        return t.length > 0 && t !== 'N/A';
      }
      if (Array.isArray(v)) return v.length > 0;
      return true;
    });
    return meaningful ?? matches[0] ?? null;
  }

  getDinValue(platformResult: any, ...keys: string[]): string {
    const raw = this.getRawDinFieldValue(...keys);
    if (raw !== null && raw !== undefined) {
      if (Array.isArray(raw)) {
        return raw
          .map((v: any) => String(v ?? '').trim())
          .filter(Boolean)
          .join(', ');
      }
      const text = this.utilsService.formatDisplayValue(raw);
      return text && text !== 'N/A' ? text : '';
    }
    const detail = this.getDinDetail(platformResult, ...keys);
    if (!detail || this.isEmptyValue(detail.value)) {
      return '';
    }
    if (Array.isArray(detail.value)) {
      return detail.value
        .map((v: any) => String(v ?? '').trim())
        .filter(Boolean)
        .join(', ');
    }
    const formatted = this.utilsService.formatDisplayValue(detail.value);
    return formatted && formatted !== 'N/A' ? formatted : '';
  }

  getDinDirectorName(platformResult: any): string {
    const raw =
      this.getDinValue(platformResult, 'director_name') ||
      String(this.aggregatedNames?.[0] || '').trim() ||
      '—';
    if (String(raw).trim().toLowerCase() === 'zaubacorp') {
      return 'Not found';
    }
    return this.formatDinDisplayName(raw);
  }

  getDinCompanyCount(platformResult?: any): number {
    const raw = this.getRawDinFieldValue('company_count');
    if (raw !== null && raw !== undefined && Number.isFinite(Number(raw))) {
      return Number(raw);
    }
    return this.getDinCompanies(platformResult).length;
  }

  getDinCurrentCompanies(_platformResult?: any): string[] {
    const raw = this.getRawDinFieldValue('current_companies');
    if (Array.isArray(raw)) {
      return raw.map((v: any) => String(v ?? '').trim()).filter(Boolean);
    }
    return [];
  }

  getDinRegistrySources(_platformResult?: any): string[] {
    const raw = this.getRawDinFieldValue('registry_sources');
    if (Array.isArray(raw)) {
      return raw.map((v: any) => String(v ?? '').trim()).filter(Boolean);
    }
    return [];
  }

  getDinProfileUrl(_platformResult?: any): string {
    const raw = this.getRawDinFieldValue('profile_url');
    if (typeof raw === 'string' && raw.trim() && raw.trim() !== 'N/A') {
      return raw.trim();
    }
    return '';
  }

  getDinCompanies(_platformResult?: any): Array<{
    company?: string;
    designation?: string;
    date_of_appointment?: string;
  }> {
    const fromFields = this.getRawDinFieldValue('companies');
    if (Array.isArray(fromFields) && fromFields.length) {
      return fromFields
        .map((row: any) => ({
          company: String(this.unwrapDinFieldValue(row?.company) ?? '').trim(),
          designation: String(this.unwrapDinFieldValue(row?.designation) ?? '').trim(),
          date_of_appointment: String(
            this.unwrapDinFieldValue(row?.date_of_appointment) ?? '',
          ).trim(),
        }))
        .filter((row) => Boolean(row.company));
    }
    const accounts = this.getRawDinSource()?.accounts;
    if (Array.isArray(accounts) && accounts.length) {
      return accounts
        .map((row: any) => ({
          company: String(this.unwrapDinFieldValue(row?.company) ?? '').trim(),
          designation: String(this.unwrapDinFieldValue(row?.designation) ?? '').trim(),
          date_of_appointment: String(
            this.unwrapDinFieldValue(row?.date_of_appointment) ?? '',
          ).trim(),
        }))
        .filter((row) => Boolean(row.company));
    }
    return [];
  }

  getDinTimeline(_platformResult?: any): Array<{
    company?: string;
    role?: string;
    type?: string;
    start?: string;
    end?: string;
  }> {
    const raw = this.getRawDinFieldValue('timeline');
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((row: any) => row && typeof row === 'object')
      .map((row: any) => ({
        company: String(row.company ?? '').trim(),
        role: String(row.role ?? '').trim(),
        type: String(row.type ?? '').trim(),
        start: String(row.start ?? '').trim(),
        end: String(row.end ?? '').trim(),
      }));
  }

  formatDinPeriod(start?: string, end?: string): string {
    const s = String(start || '').trim();
    const e = String(end || '').trim();
    if (s && e) return `${s} – ${e}`;
    if (s) return s;
    if (e) return e;
    return '—';
  }

  formatDinTypeLabel(type?: string): string {
    const t = String(type || '').trim();
    if (!t) return '—';
    if (t === 'currentCompany') return 'Current';
    if (t === 'pastCompany') return 'Past';
    return t
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (m) => m.toUpperCase());
  }

  private formatDinDisplayName(value: string): string {
    const s = String(value || '').trim();
    if (!s || s === '—') return s;
    const letters = s.replace(/[^A-Za-z]/g, '');
    if (letters && letters === letters.toUpperCase()) {
      return s
        .toLowerCase()
        .replace(/\b([a-z])/g, (m) => m.toUpperCase());
    }
    return s;
  }

  getDinStatusLabel(platformResult: any): string {
    return platformResult?.notFound ? 'Not found' : 'Found';
  }

  isDinStatusActive(platformResult: any): boolean {
    return !platformResult?.notFound;
  }

  getLinkDisplayText(value: any): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '';
    }
    try {
      const url = new URL(this.utilsService.toLink(raw));
      const path = `${url.host}${url.pathname}`.replace(/\/$/, '');
      return path || raw;
    } catch {
      return raw.replace(/^https?:\/\//i, '').replace(/\/$/, '');
    }
  }

  /** Object-like text that should use the same dark record card as the web view. */
  hasStructuredFieldValue(detail: any): boolean {
    if (!detail || this.isEmptyValue(detail.value)) {
      return false;
    }
    return this.utilsService.hasStructuredDisplayPairs(
      this.utilsService.formatDisplayValue(detail.value),
    );
  }

  isActiveStatusValue(detail: any): boolean {
    const label = String(detail?.label || '')
      .toLowerCase()
      .trim();
    if (!label.includes('status')) {
      return false;
    }
    const value = String(detail?.value ?? '')
      .toLowerCase()
      .trim();
    return value === 'active' || value === 'yes' || value === 'true';
  }

  getDisplayListItems(detail: any): string[] {
    const values = Array.isArray(detail?.value) ? detail.value : [];
    const label = String(detail?.label || '')
      .trim()
      .toLowerCase();

    return values
      .map((item: any) => this.utilsService.formatListItemForDisplay(item, label))
      .filter((item: string) => item.length > 0);
  }

  formatObjectValue(value: any): string {
    return this.utilsService.formatKeyValueObject(value);
  }

  formatDateValue(value: any): string {
    if (!value) {
      return '';
    }
    try {
      if (this.utilsService.isIsoDate(value)) {
        return this.utilsService.formatDateTime(value, 'shortDate');
      }
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return String(value);
      }
      return date.toLocaleDateString();
    } catch {
      return String(value);
    }
  }

  isEmptyValue(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length === 0 || this.isBlankPlaceholder(trimmed);
    }
    return false;
  }

  private isBlankPlaceholder(value: any): boolean {
    return typeof value === 'string' && value.trim() === '()';
  }

  private readQueryValue(source: any): string {
    return readScalarQueryValue(source, [
      this.typeConfig.queryField,
      ...this.typeConfig.queryFallbacks,
    ]);
  }

  private withQueryValue(source: any, queryValue: string): any {
    return {
      ...source,
      [this.typeConfig.queryField]:
        this.readQueryValue(source) || queryValue || undefined,
    };
  }

  private resolveSearchResult(
    routeId: string,
    querySearchId: string,
    queryValue: string,
  ): any | null {
    const fromHistory = history.state?.searchResult;
    if (fromHistory) {
      return this.withQueryValue(fromHistory, queryValue);
    }

    if (routeId) {
      try {
        const raw = localStorage.getItem(
          `${PRINTABLE_STATE_PREFIX}${this.searchType}-${routeId}`,
        );
        if (raw) {
          const parsed = JSON.parse(raw);
          const fromStorage = parsed?.searchResult ?? parsed ?? null;
          if (fromStorage) {
            const resolved = this.readQueryValue(fromStorage) || queryValue;
            if (resolved) {
              localStorage.setItem(
                `${PRINTABLE_STATE_PREFIX}${this.searchType}-${routeId}-query`,
                resolved,
              );
            }
            localStorage.removeItem(
              `${PRINTABLE_STATE_PREFIX}${this.searchType}-${routeId}`,
            );
            return this.withQueryValue(fromStorage, resolved);
          }
        }
      } catch {
        // fall through to query-param bootstrap
      }
    }

    const storedQuery = routeId
      ? localStorage.getItem(
        `${PRINTABLE_STATE_PREFIX}${this.searchType}-${routeId}-query`,
      )
      : null;

    if (querySearchId || storedQuery || queryValue) {
      return {
        done: true,
        osint_search_id: querySearchId || undefined,
        job_id: routeId || undefined,
        [this.typeConfig.queryField]: queryValue || storedQuery || undefined,
      };
    }

    return null;
  }

  private getCompletedSearchResult(): void {
    const originalQuery =
      this.readQueryValue(this.selectedSearchResult) ||
      this.route.snapshot.queryParamMap.get(this.typeConfig.queryParamKey) ||
      '';

    const persistQuery = (resolvedQuery: string) => {
      const routeId = this.route.snapshot.paramMap.get('id') || '';
      if (resolvedQuery && routeId) {
        localStorage.setItem(
          `${PRINTABLE_STATE_PREFIX}${this.searchType}-${routeId}-query`,
          resolvedQuery,
        );
      }
    };

    const applyPayload = (payload: any): boolean => {
      if (!payload) {
        return false;
      }

      if (this.isVendorShapedResult(payload)) {
        const resolvedQuery =
          originalQuery ||
          this.readQueryValue(payload) ||
          this.readScalarText(payload?.username) ||
          this.readScalarText(payload?.query);
        this.selectedSearchResult = {
          ...this.selectedSearchResult,
          ...payload,
          [this.typeConfig.queryField]: resolvedQuery,
          osint_search_id:
            payload?.osint_search_id ||
            this.searchId ||
            this.selectedSearchResult?.osint_search_id,
        };
        persistQuery(resolvedQuery);
        this.processVendorSearchResult(payload);
        return true;
      }

      if (payload?.data_list?.length) {
        const completedSearchResult = payload.data_list[0];
        const resolvedQuery =
          originalQuery ||
          this.readQueryValue(completedSearchResult) ||
          this.readQueryValue(payload);
        this.selectedSearchResult = {
          ...completedSearchResult,
          [this.typeConfig.queryField]: resolvedQuery,
          osint_search_id:
            completedSearchResult?.osint_search_id || this.searchId,
        };
        persistQuery(resolvedQuery);
        this.processSearchResult(payload);
        return true;
      }

      return false;
    };

    if (!this.searchId) {
      if (!applyPayload(this.selectedSearchResult)) {
        this.noRecords = true;
        this.pagesReady = true;
      }
      return;
    }

    this.loadingCompletedSearchResult = true;

    this.loadSubscription?.unsubscribe();
    const result$ =
      this.searchType === 'gst'
        ? this.gstSearchService.getCompletedSearchResult(this.searchId)
        : this.searchType === 'din'
          ? this.dinSearchService.getCompletedSearchResult(this.searchId)
          : this.osintSearchService.getCompletedSearchResult(
              this.typeConfig.apiType,
              this.searchId,
            );

    this.loadSubscription = result$.subscribe({
      next: (response) => {
        let rawSearchResultData = response?.data;
        if (this.searchType === 'gst') {
          rawSearchResultData = this.normalizeGstPrintablePayload(
            rawSearchResultData,
          );
        } else if (this.searchType === 'din') {
          rawSearchResultData = this.normalizeDinPrintablePayload(
            rawSearchResultData,
          );
        }
        if (
          !applyPayload(rawSearchResultData) &&
          !applyPayload(this.selectedSearchResult)
        ) {
          this.noRecords = true;
          this.pagesReady = true;
        }

        this.loadingCompletedSearchResult = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error(error);
        if (!applyPayload(this.selectedSearchResult)) {
          this.noRecords = true;
          this.pagesReady = true;
        }
        this.loadingCompletedSearchResult = false;
        this.cdr.detectChanges();
      },
    });
  }

  private normalizeGstPrintablePayload(raw: any) {
    if (!raw) {
      return raw;
    }
    const rows = Array.isArray(raw.data_list) ? raw.data_list : [];
    const data_list = rows.map((row: any) => {
      const gstData = row?.gst_data || {};
      return {
        ...row,
        sources: gstData.sources || row.sources || {},
        name: Array.isArray(gstData.name)
          ? gstData.name
          : gstData.company_name
            ? [gstData.company_name]
            : row.name || [],
        email: Array.isArray(gstData.emails)
          ? gstData.emails
          : gstData.email
            ? [gstData.email]
            : row.email || [],
        searched_gst: gstData.gstin || row.searched_gst,
        gst_search_id: row.gst_search_id,
        osint_search_id: row.gst_search_id || row.osint_search_id,
      };
    });
    return { ...raw, data_list };
  }

  private normalizeDinPrintablePayload(raw: any) {
    if (!raw) {
      return raw;
    }
    const rows = Array.isArray(raw.data_list) ? raw.data_list : [];
    const data_list = rows.map((row: any) => {
      const dinData = row?.din_data || {};
      return {
        ...row,
        sources: dinData.sources || row.sources || {},
        name: Array.isArray(dinData.name)
          ? dinData.name
          : dinData.director_name
            ? [dinData.director_name]
            : row.name || [],
        email: Array.isArray(dinData.emails)
          ? dinData.emails
          : dinData.email
            ? [dinData.email]
            : row.email || [],
        searched_din: dinData.din || row.searched_din,
        din_search_id: row.din_search_id,
        osint_search_id: row.din_search_id || row.osint_search_id,
      };
    });
    return { ...raw, data_list };
  }

  private readScalarText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private isVendorShapedResult(data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }
    if (Array.isArray(data.data_list) && data.data_list.length) {
      return false;
    }
    return Array.isArray(data.results);
  }

  private processVendorSearchResult(data: any): void {
    this.noRecords = false;
    this.pagesReady = false;
    this.loadingImages = false;

    const results = Array.isArray(data?.results) ? data.results : [];
    this.searchResultDetails = results
      .map((item: any) => this.mapVendorResultToPlatformCard(item))
      .sort(
        (a: any, b: any) =>
          this.getPlatformDisplayRank(a) - this.getPlatformDisplayRank(b),
      );

    this.aggregatedNames = Array.isArray(data?.names)
      ? [...data.names]
      : Array.isArray(data?.name)
        ? [...data.name]
        : [];
    this.aggregatedEmails = this.normalizeVendorEmails(data?.emails);
    this.aggregatedEmailHints = this.normalizeVendorEmails(
      data?.email_hint ?? data?.email_hints,
    );
    this.searchResultImages = this.buildImagesFromVendorPayload(data);

    const hasContent =
      this.searchResultDetails.length > 0 ||
      this.searchResultImages.length > 0 ||
      this.aggregatedNames.length > 0 ||
      this.aggregatedEmails.length > 0 ||
      this.aggregatedEmailHints.length > 0;

    if (!hasContent) {
      this.noRecords = true;
      this.pagesReady = true;
      return;
    }

    this.buildMeasureBlocks();
    this.schedulePagination();
  }

  private mapVendorResultToPlatformCard(item: any): any {
    const status = String(item?.status || '').toLowerCase();
    const platformKey = String(item?.platform || item?.name || 'unknown');
    const displayName = String(item?.name || item?.platform || 'Unknown');
    const profile =
      item?.profile && typeof item.profile === 'object' ? item.profile : {};
    const details: any[] = [];

    if (profile.display_name || profile.name) {
      details.push({
        label: 'Name',
        value: profile.display_name || profile.name,
        type: 'string',
      });
    }
    if (item?.profile_url) {
      details.push({
        label: 'Profile Url',
        value: item.profile_url,
        type: 'link',
      });
    }
    Object.entries(profile).forEach(([key, value]) => {
      if (
        [
          'display_name',
          'name',
          'avatar',
          'avatar_full',
          'profile_url',
          'url',
        ].includes(key)
      ) {
        return;
      }
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value === 'object') {
        return;
      }
      const lower = key.toLowerCase();
      details.push({
        label: this.humanizeVendorFieldLabel(key),
        value,
        type:
          typeof value === 'boolean'
            ? 'boolean'
            : typeof value === 'number'
              ? 'number'
              : lower.includes('url') || lower.includes('link')
                ? 'link'
                : 'string',
      });
    });

    return {
      platform: displayName,
      platformKey,
      category: item?.category || this.getPlatformCategory(displayName),
      status,
      notFound: status === 'not_found',
      isUnknown: status === 'unknown',
      emptyAccount:
        status === 'found' && details.length === 0 && !item?.profile_url,
      icon: createPlatformIconDataUrl(platformKey),
      profile_url: item?.profile_url || null,
      profile,
      imageUrl: profile.avatar_full || profile.avatar || item?.imageUrl || null,
      entries: details.length
        ? [
          {
            kind: 'profile',
            details,
          },
        ]
        : [],
    };
  }

  private buildImagesFromVendorPayload(vendor: any): SearchImage[] {
    const images: SearchImage[] = [];
    const seen = new Set<string>();

    const pushImage = (src: string, platform: string) => {
      const url = String(src || '').trim();
      if (!url || seen.has(url)) {
        return;
      }
      if (!(url.startsWith('http') || url.startsWith('data:image/'))) {
        return;
      }
      seen.add(url);
      images.push({
        platform,
        src: url,
        name: platform,
        height: images.length === 0 ? 200 : 150,
        icon: createPlatformIconDataUrl(platform),
      });
    };

    (Array.isArray(vendor?.results) ? vendor.results : []).forEach(
      (item: any) => {
        if (String(item?.status || '').toLowerCase() !== 'found') {
          return;
        }
        const platform = String(item?.name || item?.platform || 'Platform');
        const profile =
          item?.profile && typeof item.profile === 'object' ? item.profile : {};
        const src = profile.avatar_full || profile.avatar || item?.imageUrl;
        if (src) {
          pushImage(src, platform);
        }
      },
    );

    (Array.isArray(vendor?.avatars) ? vendor.avatars : []).forEach(
      (avatar: string, index: number) => {
        pushImage(avatar, index === 0 ? 'Avatar' : `Avatar ${index + 1}`);
      },
    );

    return images;
  }

  private normalizeVendorEmails(emails: any): string[] {
    if (!Array.isArray(emails)) {
      return [];
    }
    const out: string[] = [];
    for (const entry of emails) {
      if (typeof entry === 'string' && entry.trim()) {
        out.push(entry.trim());
        continue;
      }
      if (entry && typeof entry === 'object') {
        const value =
          entry.email || entry.address || entry.value || entry.mail || null;
        if (typeof value === 'string' && value.trim()) {
          out.push(value.trim());
        }
      }
    }
    return Array.from(new Set(out));
  }

  private humanizeVendorFieldLabel(key: string): string {
    return String(key || '')
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private processSearchResult(searchResult: any): void {
    this.noRecords = false;
    this.pagesReady = false;
    const sourceKeysWithNoAccount = this.getSourceKeysWithNoAccount(searchResult);
    const processedResult = this.searchResultProcessorService.process(
      searchResult.data_list[0],
    );

    this.searchResultDetails = (processedResult.platforms ?? []).map(
      (platform: any) => {
        const status = String(platform?.status || '').toLowerCase();
        return {
          ...platform,
          notFound:
            status === 'not_found' ||
            sourceKeysWithNoAccount.has(
              this.normalizePlatformNameForLookup(platform?.platformKey || platform?.platform),
            ),
          isUnknown: status === 'unknown' || !!platform?.isUnknown,
        };
      },
    );
    this.searchResultDetails.sort(
      (a, b) => this.getPlatformDisplayRank(a) - this.getPlatformDisplayRank(b),
    );
    this.updateAggregatedData();
    this.extractImagesFromSources(searchResult);
  }

  private extractImagesFromSources(apiResponseData: any): void {
    this.loadingImages = true;
    this.imagesSubscription?.unsubscribe();
    this.imagesSubscription = this.searchResultProcessorService
      .extractImagesFromSources(apiResponseData)
      .subscribe({
        next: (images) => {
          this.searchResultImages = images;
          this.loadingImages = false;
          this.buildMeasureBlocks();
          this.schedulePagination();
        },
        error: (error) => {
          console.error('Error extracting images:', error);
          this.searchResultImages = [];
          this.loadingImages = false;
          this.buildMeasureBlocks();
          this.schedulePagination();
        },
      });
  }

  private buildMeasureBlocks(): void {
    const blocks: PrintBlock[] = [
      { key: 'header', type: 'header' },
      { key: 'status', type: 'status' },
    ];

    // Match status-card order: Photos → Names → Emails → Email Hints, then platforms.
    // GST/DIN have no photos on the web result page — keep export consistent.
    const remainingNames = [...(this.aggregatedNames ?? [])];
    let namesHeadingShownInPhotoFill = false;
    if (!this.isRegistryPrintable && this.searchResultImages.length) {
      const images = this.searchResultImages;
      const fullRowCount = Math.floor(images.length / PHOTOS_PER_ROW);
      for (let row = 0; row < fullRowCount; row++) {
        const start = row * PHOTOS_PER_ROW;
        blocks.push({
          key: `photos-row-${start}`,
          type: 'photos',
          images: images.slice(start, start + PHOTOS_PER_ROW),
          showPhotosHeading: row === 0,
        });
      }

      const remainderStart = fullRowCount * PHOTOS_PER_ROW;
      const remainderImages = images.slice(remainderStart);
      if (remainderImages.length) {
        // Incomplete row (1–3 photos): fill leftover space with a Names sidebar.
        const fillCount = Math.min(
          remainingNames.length,
          this.namesToFillBesidePhotos(remainderImages.length),
        );
        const fillNames = remainingNames.splice(0, fillCount);
        if (fillNames.length) {
          namesHeadingShownInPhotoFill = true;
        }
        blocks.push({
          key: `photos-row-${remainderStart}`,
          type: 'photos',
          images: remainderImages,
          fillNames: fillNames.length ? fillNames : undefined,
          showPhotosHeading: fullRowCount === 0,
        });
      }
    }

    if (remainingNames.length) {
      for (let i = 0; i < remainingNames.length; i += NAMES_PER_CHUNK) {
        blocks.push({
          key: `names-${i}`,
          type: 'names',
          names: remainingNames.slice(i, i + NAMES_PER_CHUNK),
          // Heading already shown in the photo-row Names sidebar when filled.
          showNamesHeading: i === 0 && !namesHeadingShownInPhotoFill,
        });
      }
    }

    if (this.aggregatedEmails.length) {
      for (let i = 0; i < this.aggregatedEmails.length; i += EMAILS_PER_CHUNK) {
        blocks.push({
          key: `emails-${i}`,
          type: 'emails',
          emails: this.aggregatedEmails.slice(i, i + EMAILS_PER_CHUNK),
          showEmailsHeading: i === 0,
        });
      }
    }

    if (!this.isRegistryPrintable && this.aggregatedEmailHints.length) {
      blocks.push({ key: 'emailHints', type: 'emailHints' });
    }

    this.searchResultDetails.forEach((platform, index) => {
      blocks.push({
        key: `platform-${index}-${this.normalizePlatformNameForLookup(platform?.platform)}`,
        type: 'platform',
        platform,
      });
    });

    // Appendix: same recovered photos again at natural/original size (no fixed crop).
    if (!this.isRegistryPrintable && this.searchResultImages.length) {
      this.searchResultImages.forEach((image, index) => {
        blocks.push({
          key: `original-photo-${index}`,
          type: 'originalPhoto',
          image,
          showOriginalPhotosHeading: index === 0,
        });
      });
    }

    this.measureBlocks = blocks;
    this.isPaginating = true;
    this.pages = [];
    this.cdr.detectChanges();
  }

  private schedulePagination(): void {
    if (!this.viewReady) {
      this.pendingPagination = true;
      return;
    }
    this.pendingPagination = false;

    if (this.paginationTimer) {
      clearTimeout(this.paginationTimer);
    }

    // Wait for layout + images to settle before measuring block heights.
    this.paginationTimer = setTimeout(() => {
      void this.paginateMeasuredBlocks();
    }, 220);
  }

  private async paginateMeasuredBlocks(): Promise<void> {
    if (!this.measureBlocks.length) {
      this.pages = [];
      this.isPaginating = false;
      this.pagesReady = true;
      this.cdr.detectChanges();
      return;
    }

    await this.waitForMeasureImages();
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );

    let els = this.measureBlockEls?.toArray() ?? [];
    if (!els.length) {
      this.cdr.detectChanges();
      els = this.measureBlockEls?.toArray() ?? [];
    }

    const heights = new Map<string, number>();
    els.forEach((ref) => {
      const el = ref.nativeElement;
      const key = el.getAttribute('data-block-key');
      if (!key) {
        return;
      }
      const style = window.getComputedStyle(el);
      const marginTop = Number.parseFloat(style.marginTop) || 0;
      const marginBottom = Number.parseFloat(style.marginBottom) || 0;
      heights.set(key, Math.ceil(el.offsetHeight + marginTop + marginBottom));
    });

    const maxContentHeight = CONTENT_MAX_HEIGHT_PX - PAGE_PACK_SAFETY_PX;
    const rawPages: Array<{ blocks: PrintBlock[]; allowGrow: boolean }> = [];
    let currentBlocks: PrintBlock[] = [];
    let usedHeight = 0;
    let currentAllowGrow = false;

    const flushPage = () => {
      if (!currentBlocks.length) {
        return;
      }
      rawPages.push({
        blocks: this.coalescePageBlocks(currentBlocks),
        allowGrow: currentAllowGrow,
      });
      currentBlocks = [];
      usedHeight = 0;
      currentAllowGrow = false;
    };

    const getHeight = (block: PrintBlock): number =>
      Math.max(heights.get(block.key) || 0, 1);

    let index = 0;
    while (index < this.measureBlocks.length) {
      const block = this.measureBlocks[index];

      // Pack a consecutive run of platform cards into multi-column pages.
      if (block.type === 'platform') {
        const platformRun: PrintBlock[] = [];
        while (
          index < this.measureBlocks.length &&
          this.measureBlocks[index].type === 'platform'
        ) {
          platformRun.push(this.measureBlocks[index]);
          index++;
        }

        let remainingPlatforms = [...platformRun];
        while (remainingPlatforms.length) {
          const available = maxContentHeight - usedHeight;
          const fitsAnyAlone = remainingPlatforms.some(
            (item) => getHeight(item) <= available,
          );

          // Remaining page space can't fit any card → start a fresh page.
          if (currentBlocks.length > 0 && !fitsAnyAlone) {
            flushPage();
            continue;
          }

          const packedResult = this.packPlatformsDensely(
            remainingPlatforms,
            available,
            getHeight,
            currentBlocks.length === 0,
          );

          if (!packedResult.packed.length) {
            if (currentBlocks.length > 0) {
              flushPage();
              continue;
            }
            // Oversized single card: place alone and allow the sheet to grow.
            const [oversized, ...rest] = remainingPlatforms;
            const oversizedColumns = this.distributePlatformsToColumns(
              [oversized],
              getHeight,
            );
            currentBlocks.push({
              key: `platforms-oversize-${oversized.key}`,
              type: 'platforms',
              platforms: [oversized.platform],
              platformColumns: oversizedColumns.columns,
            });
            currentAllowGrow = true;
            remainingPlatforms = rest;
            flushPage();
            continue;
          }

          currentBlocks.push({
            key: `platforms-${rawPages.length}-${currentBlocks.length}-${packedResult.packed.length}`,
            type: 'platforms',
            platforms: packedResult.packed.map((item) => item.platform),
            platformColumns: packedResult.columns,
          });
          usedHeight += packedResult.layoutHeight;
          currentAllowGrow = packedResult.allowGrow;
          remainingPlatforms = packedResult.leftover;

          if (remainingPlatforms.length) {
            flushPage();
          }
        }
        continue;
      }

      // Non-platform blocks: simple vertical stack.
      const blockHeight = getHeight(block);
      const exceedsPageAlone = blockHeight > maxContentHeight;
      const fits =
        currentBlocks.length === 0 ||
        (!exceedsPageAlone && usedHeight + blockHeight <= maxContentHeight);

      if (!fits) {
        flushPage();
      }

      currentBlocks.push(block);
      usedHeight += blockHeight;
      index++;

      if (exceedsPageAlone) {
        currentAllowGrow = true;
        flushPage();
      }
    }

    flushPage();

    this.pages = rawPages.map((page, index) => ({
      pageNumber: index + 1,
      blocks: page.blocks,
      allowGrow: page.allowGrow,
    }));
    this.isPaginating = false;
    this.pagesReady = true;
    this.cdr.detectChanges();

    // After paint, verify real DOM height and grow any page that would clip.
    setTimeout(() => this.verifyRenderedPagesFit(), 80);
  }

  private verifyRenderedPagesFit(): void {
    const sheets = Array.from(
      document.querySelectorAll('.a4-preview-sheet'),
    ) as HTMLElement[];
    if (!sheets.length || sheets.length !== this.pages.length) {
      return;
    }

    let changed = false;
    sheets.forEach((sheet, index) => {
      const body = sheet.querySelector('.a4-page-body') as HTMLElement | null;
      if (!body || this.pages[index]?.allowGrow) {
        return;
      }

      // Content area inside padding; footer is outside body.
      if (body.scrollHeight > CONTENT_MAX_HEIGHT_PX) {
        this.pages[index] = {
          ...this.pages[index],
          allowGrow: true,
        };
        changed = true;
      }
    });

    if (changed) {
      this.pages = [...this.pages];
      this.cdr.detectChanges();
    }
  }

  /**
   * Choose and reorder platform cards to fill available page height as much as
   * possible. Columns are assigned with the same algorithm used for rendering,
   * so measured height matches on-page height (no clipping from CSS columns).
   */
  private packPlatformsDensely(
    remaining: PrintBlock[],
    availableHeight: number,
    getHeight: (block: PrintBlock) => number,
    allowGrowIfEmptyPage: boolean,
  ): {
    packed: PrintBlock[];
    leftover: PrintBlock[];
    allowGrow: boolean;
    columns: any[][];
    layoutHeight: number;
  } {
    if (!remaining.length) {
      return {
        packed: [],
        leftover: [],
        allowGrow: false,
        columns: [],
        layoutHeight: 0,
      };
    }

    const foundish = remaining.filter((block) => !block.platform?.notFound);
    const notFound = remaining.filter((block) => !!block.platform?.notFound);
    if (foundish.length && notFound.length) {
      const foundPack = this.packPlatformsDensely(
        foundish,
        availableHeight,
        getHeight,
        allowGrowIfEmptyPage,
      );
      const leftoverFoundAndNotFound = remaining.filter(
        (block) =>
          foundPack.leftover.includes(block) || !!block.platform?.notFound,
      );

      if (foundPack.leftover.length || !foundPack.packed.length) {
        return {
          ...foundPack,
          leftover: leftoverFoundAndNotFound,
        };
      }

      const remainingHeight = Math.max(
        0,
        availableHeight - foundPack.layoutHeight,
      );
      const notFoundPack = this.packPlatformsDensely(
        notFound,
        remainingHeight,
        getHeight,
        false,
      );
      if (!notFoundPack.packed.length) {
        return {
          ...foundPack,
          leftover: notFound,
        };
      }

      const combinedPacked = [...foundPack.packed, ...notFoundPack.packed];
      const columns = this.appendPlatformsToColumns(
        foundPack.columns,
        foundPack.packed,
        notFoundPack.packed,
        getHeight,
      );
      const layoutHeight = this.measureColumnStackHeight(
        columns,
        combinedPacked,
        getHeight,
      );
      if (layoutHeight <= availableHeight) {
        return {
          packed: combinedPacked,
          leftover: notFoundPack.leftover,
          allowGrow: foundPack.allowGrow,
          columns,
          layoutHeight,
        };
      }

      return {
        ...foundPack,
        leftover: notFound,
      };
    }

    const byTallest = [...remaining].sort(
      (a, b) => getHeight(b) - getHeight(a),
    );

    const fitsAlone = byTallest.filter(
      (item) => getHeight(item) <= availableHeight,
    );

    if (!fitsAlone.length) {
      if (allowGrowIfEmptyPage) {
        const distributed = this.distributePlatformsToColumns(
          [byTallest[0]],
          getHeight,
        );
        return {
          packed: [byTallest[0]],
          leftover: byTallest.slice(1),
          allowGrow: true,
          columns: distributed.columns,
          layoutHeight: distributed.height,
        };
      }
      return {
        packed: [],
        leftover: remaining,
        allowGrow: false,
        columns: [],
        layoutHeight: 0,
      };
    }

    // First-fit decreasing: try tall cards first so short ones fill gaps later.
    const packed: PrintBlock[] = [];
    const leftover: PrintBlock[] = [];

    for (const card of byTallest) {
      const trial = [...packed, card];
      const trialLayout = this.distributePlatformsToColumns(trial, getHeight);
      if (trialLayout.height <= availableHeight) {
        packed.push(card);
      } else {
        leftover.push(card);
      }
    }

    // Second pass: retry leftovers after denser set is known.
    const stillLeftover: PrintBlock[] = [];
    for (const card of leftover) {
      const trial = [...packed, card];
      const trialLayout = this.distributePlatformsToColumns(trial, getHeight);
      if (trialLayout.height <= availableHeight) {
        packed.push(card);
      } else {
        stillLeftover.push(card);
      }
    }

    const finalLayout = this.distributePlatformsToColumns(packed, getHeight);

    return {
      packed,
      leftover: stillLeftover,
      allowGrow: false,
      columns: finalLayout.columns,
      layoutHeight: finalLayout.height,
    };
  }

  /**
   * Append not-found cards to the shortest columns so they stay last instead of
   * being mixed into found cards by tallest-first packing.
   *
   * Important: expand to up to 3 columns based on total card count. Otherwise a
   * single found card (1 column) forces every not-found card into one full-width
   * stack.
   */
  private appendPlatformsToColumns(
    existingColumns: any[][],
    existingBlocks: PrintBlock[],
    extraBlocks: PrintBlock[],
    getHeight: (block: PrintBlock) => number,
  ): any[][] {
    const totalCards = existingBlocks.length + extraBlocks.length;
    const targetCols = Math.max(
      1,
      Math.min(PLATFORM_COLUMN_COUNT, totalCards || 1),
    );

    // Start from existing found-card columns, then grow to the target width.
    const columns: any[][] = existingColumns.length
      ? existingColumns.map((column) => [...column])
      : [];
    while (columns.length < targetCols) {
      columns.push([]);
    }

    // If found cards were packed into fewer columns than we need now, spread
    // them across targetCols so the page uses the full width.
    if (existingColumns.length > 0 && existingColumns.length < targetCols) {
      const redistributed = this.distributePlatformsToColumns(
        existingBlocks,
        getHeight,
      ).columns;
      for (let i = 0; i < targetCols; i++) {
        columns[i] = [...(redistributed[i] || [])];
      }
    }

    const heights = columns.map((column) =>
      column.reduce((sum, platform) => {
        const block = existingBlocks.find((item) => item.platform === platform);
        return sum + (block ? getHeight(block) : 0);
      }, 0),
    );

    for (const item of extraBlocks) {
      let shortest = 0;
      for (let i = 1; i < columns.length; i++) {
        if (heights[i] < heights[shortest]) {
          shortest = i;
        }
      }
      columns[shortest].push(item.platform);
      heights[shortest] += getHeight(item);
    }

    return columns;
  }

  private measureColumnStackHeight(
    columns: any[][],
    blocks: PrintBlock[],
    getHeight: (block: PrintBlock) => number,
  ): number {
    if (!columns.length) {
      return 0;
    }
    const heights = columns.map((column) =>
      column.reduce((sum, platform) => {
        const block = blocks.find((item) => item.platform === platform);
        return sum + (block ? getHeight(block) : 0);
      }, 0),
    );
    return Math.max(0, ...heights);
  }

  /**
   * Place cards into N columns (tallest first → shortest column).
   * Render uses the same buckets, so layout height is exact.
   */
  private distributePlatformsToColumns(
    items: PrintBlock[],
    getHeight: (block: PrintBlock) => number,
  ): { columns: any[][]; height: number } {
    if (!items.length) {
      return { columns: [], height: 0 };
    }

    const cols = Math.max(1, Math.min(PLATFORM_COLUMN_COUNT, items.length));
    const columns: any[][] = Array.from({ length: cols }, () => []);
    const columnHeights = new Array(cols).fill(0);
    const ordered = [...items].sort((a, b) => getHeight(b) - getHeight(a));

    for (const item of ordered) {
      let shortest = 0;
      for (let i = 1; i < cols; i++) {
        if (columnHeights[i] < columnHeights[shortest]) {
          shortest = i;
        }
      }
      columns[shortest].push(item.platform);
      columnHeights[shortest] += getHeight(item);
    }

    return {
      columns,
      height: Math.max(...columnHeights),
    };
  }

  private estimateMultiColumnHeight(
    itemHeights: number[],
    columnCount: number,
  ): number {
    if (!itemHeights.length) {
      return 0;
    }
    const cols = Math.max(1, Math.min(columnCount, itemHeights.length));
    const columnHeights = new Array(cols).fill(0);
    const ordered = [...itemHeights].sort((a, b) => b - a);
    for (const height of ordered) {
      let shortest = 0;
      for (let i = 1; i < cols; i++) {
        if (columnHeights[i] < columnHeights[shortest]) {
          shortest = i;
        }
      }
      columnHeights[shortest] += height;
    }
    return Math.max(...columnHeights);
  }

  /**
   * Merge consecutive single-platform / names / emails blocks on the same page
   * so each section fills one box completely before the next starts.
   */
  private coalescePageBlocks(blocks: PrintBlock[]): PrintBlock[] {
    const result: PrintBlock[] = [];
    let platformBuffer: any[] = [];
    let namesBuffer: { names: string[]; showHeading: boolean } | null = null;
    let emailsBuffer: { emails: string[]; showHeading: boolean } | null = null;

    const flushPlatforms = () => {
      if (!platformBuffer.length) {
        return;
      }
      result.push({
        key: `platforms-page-${result.length}-${platformBuffer.length}`,
        type: 'platforms',
        platforms: platformBuffer,
      });
      platformBuffer = [];
    };

    const flushNames = () => {
      if (!namesBuffer?.names.length) {
        namesBuffer = null;
        return;
      }
      result.push({
        key: `names-page-${result.length}-${namesBuffer.names.length}`,
        type: 'names',
        names: namesBuffer.names,
        showNamesHeading: namesBuffer.showHeading,
      });
      namesBuffer = null;
    };

    const flushEmails = () => {
      if (!emailsBuffer?.emails.length) {
        emailsBuffer = null;
        return;
      }
      result.push({
        key: `emails-page-${result.length}-${emailsBuffer.emails.length}`,
        type: 'emails',
        emails: emailsBuffer.emails,
        showEmailsHeading: emailsBuffer.showHeading,
      });
      emailsBuffer = null;
    };

    const flushAll = () => {
      flushPlatforms();
      flushNames();
      flushEmails();
    };

    for (const block of blocks) {
      if (block.type === 'platform' && block.platform) {
        flushNames();
        flushEmails();
        platformBuffer.push(block.platform);
        continue;
      }

      if (block.type === 'names' && block.names?.length) {
        flushPlatforms();
        flushEmails();
        if (!namesBuffer) {
          namesBuffer = {
            names: [...block.names],
            showHeading: !!block.showNamesHeading,
          };
        } else {
          namesBuffer.names.push(...block.names);
          namesBuffer.showHeading =
            namesBuffer.showHeading || !!block.showNamesHeading;
        }
        continue;
      }

      if (block.type === 'emails' && block.emails?.length) {
        flushPlatforms();
        flushNames();
        if (!emailsBuffer) {
          emailsBuffer = {
            emails: [...block.emails],
            showHeading: !!block.showEmailsHeading,
          };
        } else {
          emailsBuffer.emails.push(...block.emails);
          emailsBuffer.showHeading =
            emailsBuffer.showHeading || !!block.showEmailsHeading;
        }
        continue;
      }

      flushAll();
      result.push(block);
    }

    flushAll();
    return result;
  }

  private waitForMeasureImages(): Promise<void> {
    const host = document.querySelector('.a4-measure-host');
    if (!host) {
      return Promise.resolve();
    }

    const images = Array.from(host.querySelectorAll('img'));
    if (!images.length) {
      return Promise.resolve();
    }

    return Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }
            const done = () => resolve();
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
          }),
      ),
    ).then(() => undefined);
  }

  private getSourceKeysWithNoAccount(searchResult: any): Set<string> {
    const hiddenSources = new Set<string>();
    const sources = searchResult?.data_list?.[0]?.sources;

    if (!sources || typeof sources !== 'object') {
      return hiddenSources;
    }

    Object.entries(sources).forEach(([sourceKey, sourceValue]) => {
      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        (sourceValue as any).has_account === false
      ) {
        hiddenSources.add(this.normalizePlatformNameForLookup(sourceKey));
      }
    });

    return hiddenSources;
  }

  private getPlatformDisplayRank(platform: any): number {
    if (platform?.notFound) {
      return 4;
    }
    if (platform?.isUnknown) {
      return 3;
    }
    if (this.platformHasPhotoData(platform)) {
      return 0;
    }
    if (this.platformHasProfileData(platform)) {
      return 1;
    }
    return 2;
  }

  private platformHasPhotoData(platform: any): boolean {
    if (!platform || platform.notFound || platform.isUnknown) {
      return false;
    }
    if (this.getPlatformImageForCard(platform)?.src) {
      return true;
    }
    if (String(platform.imageUrl || '').trim()) {
      return true;
    }
    const profile = platform.profile;
    if (profile && typeof profile === 'object') {
      return Boolean(
        String(profile.avatar_full || profile.avatar || '').trim(),
      );
    }
    return false;
  }

  private platformHasProfileData(platform: any): boolean {
    if (
      !platform ||
      platform.notFound ||
      platform.isUnknown ||
      platform.emptyAccount
    ) {
      return false;
    }
    if (this.getCardDescription(platform)) {
      return true;
    }
    return (this.getCardBodyDetails(platform) ?? []).some(
      (detail) => !this.isEmptyValue(detail?.value),
    );
  }

  private normalizePlatformNameForLookup(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
  }

  private updateAggregatedData(): void {
    const rawNames = Array.isArray(this.selectedSearchResult?.name)
      ? this.selectedSearchResult.name
      : [];
    this.aggregatedNames = this.isDinPrintable
      ? rawNames.map((name: unknown) =>
          String(name ?? '').trim().toLowerCase() === 'zaubacorp'
            ? 'Not found'
            : name,
        )
      : rawNames;
    this.aggregatedEmails = Array.isArray(this.selectedSearchResult?.email)
      ? this.selectedSearchResult.email
      : [];
    this.aggregatedEmailHints = Array.isArray(
      this.selectedSearchResult?.email_hint,
    )
      ? this.selectedSearchResult.email_hint
      : [];
  }

  private formatListItemForDisplay(item: any, detailLabel: string): string {
    return this.utilsService.formatListItemForDisplay(item, detailLabel);
  }

  static storePrintableState(
    routeId: string,
    searchResult: any,
    searchType: OsintPrintableSearchType = 'username',
  ): void {
    if (!routeId || !searchResult) {
      return;
    }
    localStorage.setItem(
      `${PRINTABLE_STATE_PREFIX}${searchType}-${routeId}`,
      JSON.stringify({ searchResult, searchType }),
    );
    const config = OSINT_PRINTABLE_TYPE_CONFIG[searchType];
    const query = readScalarQueryValue(searchResult, [
      config.queryField,
      ...config.queryFallbacks,
    ]);
    if (query) {
      localStorage.setItem(
        `${PRINTABLE_STATE_PREFIX}${searchType}-${routeId}-query`,
        query,
      );
    }
  }
}
